/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Dominik Boeck <d.boeck@techdivision.com>
 */

import React from 'react';
import PropTypes from 'prop-types';
import { ErrorBoundaryRender } from './ErrorBoundaryRender';

/**
 * ErrorBoundary Component
 * This class can be used to wrap any react components, catch all errors and render an error component instead
 * See https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: false,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to an error reporting service
    /* eslint no-console: ["error", { allow: ["error"] }] */
    console.error(error, errorInfo);
  }

  setError = (hasError) => {
    this.setState({ error: hasError });
  };

  render() {
    const { error } = this.state;
    const {
      children, fallback,
    } = this.props;
    return (
      <ErrorBoundaryRender
        error={error}
        setError={this.setError}
        fallback={fallback}
      >
        {children}
      </ErrorBoundaryRender>
    );
  }
}

export default ErrorBoundary;

ErrorBoundary.propTypes = {
  children: PropTypes.array.isRequired,
  fallback: PropTypes.object,
};

ErrorBoundary.defaultProps = {
  fallback: null,
};
